import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { WorkspaceDrawerTrigger } from "../../components/layout/WorkspaceDrawerTrigger.js";
import { cn } from "../../lib/utils.js";
import { useSettingsWorkspace } from "./SettingsWorkspaceLayout.js";

export function SettingsPageHeader({
  title,
  description,
  icon: Icon,
  className,
  showMobileNav = false,
  actions,
}: {
  title: string;
  description?: string;
  icon: LucideIcon;
  className?: string;
  showMobileNav?: boolean;
  actions?: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const { openMobileNav, mobileNavOpen } = useSettingsWorkspace();

  return (
    <header
      className={cn(
        "shrink-0 border-b border-line/60 bg-surface px-4 pt-3 pb-4 md:px-8 md:pt-4 md:pb-5",
        className,
      )}
      data-testid="settings-page-header"
    >
      <div className="mx-auto flex w-full max-w-[68rem] flex-col gap-4">
        <div className="flex min-h-[3rem] flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3.5">
            <span className="inline-grid size-11 shrink-0 place-items-center rounded-2xl bg-[#132a27] text-[#34d399] ring-1 ring-[#34d399]/25 shadow-[0_8px_20px_-10px_rgb(19_42_39/0.55)]">
              <Icon size={20} aria-hidden="true" />
            </span>
            <div className="min-w-0">
              <h1 className="m-0 truncate text-[1.25rem] font-bold leading-tight tracking-[-0.025em] text-foreground sm:text-[1.35rem]">
                {title}
              </h1>
              {description ? (
                <p className="m-0 mt-0.5 truncate text-[0.8125rem] text-muted">
                  {description}
                </p>
              ) : null}
            </div>
          </div>

          <div
            className="flex shrink-0 flex-wrap items-center gap-2"
            data-testid="settings-page-header-actions"
          >
            {actions}
            {showMobileNav ? (
              <WorkspaceDrawerTrigger
                label={t("settings.nav.mobileMenu")}
                open={mobileNavOpen}
                onClick={openMobileNav}
                testId="settings-open-mobile-nav"
                ariaLabel={t("settings.nav.mobileMenu")}
              />
            ) : null}
          </div>
        </div>
      </div>
    </header>
  );
}
